import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { DynamicGridBaseComponent } from 'src/app/base/shared/grid-base/dynamic-grid-base.component';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { UserManagementData, UserType } from '../models/user-management-data';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { AvpCustomAdaptor } from '../shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { ClientService } from '../services/client/client.service';
import { Role } from '../models/models.swagger.generated';
import { OnInit, Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { UserInfoDTO } from '../models/models.swagger.generated';
import { AuthService } from '../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { SfCommandButtonType } from '../models/syncfusion-data';
import { DynamicEditFormComponent } from '../shared/dynamic/dynamic-edit-form.component';
import { userManagementDynamicDefinition } from './user-management-dynamic.definition';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../shared/search-field/search-field.component';
import { ClientAutocompleteComponent } from '../shared/client-autocomplete/client-autocomplete.component';
import { ButtonGroupComponent } from '../shared/button-group/button-group.component';
import { EnumFilterService } from '../services/enum-filter.service';
import { UserManagementService } from '../user-management/user-management.service';
import { Subscription } from 'rxjs';

@Component({
  selector: "app-dynamic-user-management",
  templateUrl: "./dynamic-user-management.component.html",
  styleUrls: ["./dynamic-user-management.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ClientAutocompleteComponent,
    SearchFieldComponent,
    CheckBoxModule,
    GridModule,
    ToolbarModule,
    DynamicEditFormComponent,
    ButtonGroupComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class DynamicUserManagementComponent
  extends DynamicGridBaseComponent<typeof userManagementDynamicDefinition>
  implements OnInit
{
  public readonly Role = Role;
  public readonly UserType = UserType;
  public hiddenRoles: Role[] = [
    Role.None,
    Role.Admin,
    Role.WebUser,
    Role.MobileUser,
  ];

  private addUserType = UserType.Web;

  private roleSubscription: Subscription | null | undefined = null;

  constructor(
    fb: FormBuilder,
    private userManagementService: UserManagementService,
    translateService: TranslateService,
    private authService: AuthService,
    clientService: ClientService,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    enumFilterService: EnumFilterService,
  ) {
    super(
      userManagementDynamicDefinition,
      fb,
      clientService,
      translateService,
      userManagementService,
      enumFilterService,
    );
    this.gridCommands = [
      {
        type: SfCommandButtonType.Edit,
        buttonOption: {
          cssClass: "icon-btn",
          iconCss: "icon-edit e-icons",
        },
      },
    ] as CommandModel[];
    this.searchOptions = { fields: this.crudDefinition.grid.searchFields };
  }

  public ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(
        this.userManagementService,
        this.spinnerService,
      ),
    });

    this.route.queryParams.subscribe((params) => {
      const clientId = +params.clientId;
      const newSelectedClient = this.clientService?.clients?.find(
        (c) => c.id === clientId,
      );
      if (newSelectedClient) {
        this.clientService!.selectedClient = newSelectedClient;
      }
    });

    this.isActiveColumnName = "enabled";
    this.editDialogTitle = "i18n.base.user-add-modal.titleEdit";
    this.addDialogTitle = "i18n.base.user-add-modal.titleAdd";
    this.addIndexFindColumnName = "userName";
  }

  public ngOnDestroy(): void {
    this.roleSubscription?.unsubscribe();
    this.roleSubscription = null;
  }

  public addUser(isMobileUser: boolean) {
    this.addUserType = isMobileUser ? UserType.Mobile : UserType.Web;
    this.onAdd();
  }

  protected override getDataToSave(): any {
    const userManagementData = new UserManagementData();
    userManagementData.data = super.getDataToSave();
    userManagementData.type = this.gridEditForm?.value.userType ?? null;
    return userManagementData;
  }

  protected override prepareEditFormData(data: UserInfoDTO): any {
    const clientId =
      this.authService.getUserInfo()?.clientId ??
      (data.userId ? data.clientId : this.clientService?.selectedClient?.id);

    let userType = UserType.Web;
    if (!data.userId) {
      // we are in add mode
      userType = this.addUserType;
    } else if (data.roles && data.roles.length > 0) {
      userType =
        data.roles[0] === Role.MobileUser ? UserType.Mobile : UserType.Web;
    }

    // Initialize hiddenRoles based on permissions
    if (this.authService.isUserAdmin()) {
      this.hiddenRoles = [Role.None, Role.MobileUser];
    } else {
      this.hiddenRoles = [Role.None, Role.Admin, Role.MobileUser];
    }

    // Prepare data for form creation
    return {
      ...data,
      userType: userType,
      clientId,
      accessCodeValidUntil: data.accessCodeValidUntil,
      role: !!data.roles?.length ? data.roles[0] : null,
    };
  }

  protected override afterInitializeEditForm(
    originalData: UserInfoDTO,
    preparedData: any,
  ): void {
    // patch gridEditForm for userType
    if (!this.gridEditForm?.controls.userType) {
      this.gridEditForm?.addControl(
        "userType",
        new FormControl(preparedData.userType),
      );
    } else {
      this.gridEditForm?.controls.userType.setValue(preparedData.userType);
    }

    // Add dynamic validators based on user type
    if (preparedData?.userType === UserType.Mobile) {
      this.gridEditForm?.controls.phoneNumber?.setValidators([
        Validators.required,
        CustomValidatorsFactory.createPhoneValidator(),
      ]);
      if (!originalData?.userId) {
        // new mobile user -> no role required
        this.gridEditForm?.controls.role?.clearValidators();
        this.gridEditForm?.controls.role?.updateValueAndValidity();
      }
    }

    // The edit form is recreated on each dialog open, so tear down any
    // previous role -> clientId subscription before creating a new one.
    this.roleSubscription?.unsubscribe();
    this.roleSubscription =
      this.gridEditForm?.controls.role.valueChanges.subscribe(
        (role: Role | null | undefined) => {
          const clientControl = this.gridEditForm?.controls.clientId;
          if (!clientControl) {
            return;
          }
          if (role === Role.Admin) {
            clientControl.reset();
            clientControl.setValidators([]);
            clientControl.disable({ emitEvent: false });
          } else {
            if (this.canEnableClientField()) {
              clientControl.setValidators([Validators.required]);
              clientControl.enable({ emitEvent: false });
            }
          }
          clientControl.updateValueAndValidity({ emitEvent: false });
        },
      );

    // Disable clientId during edit
    if (!this.canEnableClientField()) {
      this.gridEditForm?.controls.clientId?.disable();
    }
  }

  private canEnableClientField(): boolean {
    if (this.gridEditForm?.getRawValue().userId || this.authService.getUserInfo()?.clientId) {
      return false;
    }
    return true;
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return {};
  }

  public get editFormFields() {
    if (!this.shouldHideRoleField()) {
      return this.crudDefinition.form.fields;
    }

    return this.crudDefinition.form.fields.filter(
      (field) => field.key !== "role",
    );
  }

  private shouldHideRoleField(): boolean {
    const selectedUserType =
      this.gridEditForm?.get("userType")?.value ?? this.addUserType;
    return selectedUserType === UserType.Mobile;
  }
}

